import { useEffect, useMemo, useState } from 'react';
import type { OrderablePackage } from '@mypoz/shared';
import {
  clampPackagePage,
  eligibleOrderablePackages,
  matchesPackagePick,
  ORDER_PACKAGES_PER_PAGE,
  pageOf,
} from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';

import {
  PackagePicker,
  PoolState,
  type PackageRowsProps,
} from '@/features/orders/components/PackagesStep.js';
import { useOrderablePackagePage } from '@/features/orders/useOrderablePackagePage.js';
import { usePackages } from '@/features/packages/index.js';
import { useDebounced } from '@/lib/useDebounced.js';

export interface OrderablePackagesStepProps extends PackageRowsProps {
  /** The vendor's orderable status codes; `undefined` while the catalog loads. */
  statusCodes: readonly string[] | undefined;
  /** Every ticked package, kept by the page as rows arrive — see `onSeen`. */
  selected: readonly OrderablePackage[];
  /** Hands each page's packages up, so a tick on page 1 is still known on page 3. */
  onSeen: (packages: readonly OrderablePackage[]) => void;
}

/**
 * Create order's Step 1, paged and searched by the vendor (MYP-130).
 *
 * Each page is one `package_list` request for the orderable statuses with `validStatus: 1`. The
 * search is a full tracking number — the vendor matches nothing shorter and no item names — and
 * there is no sort, because the vendor has none. "Selected" lists the ticked packages, which the
 * page already holds, so it needs no request.
 */
export function OrderablePackagesStep({
  statusCodes,
  selected,
  onSeen,
  ...rowProps
}: OrderablePackagesStepProps) {
  const { t } = useTranslation();
  const { toPackages, dictionariesStatus, listVersion } = usePackages();

  const [searchText, setSearchText] = useState('');
  const search = useDebounced(searchText);
  const [show, setShow] = useState<'all' | 'selected'>('all');
  // Each view keeps its own page, so looking at "Selected" and back asks the vendor nothing.
  const [allPage, setAllPage] = useState(1);
  const [selectedPage, setSelectedPage] = useState(1);
  const [size, setSize] = useState(ORDER_PACKAGES_PER_PAGE);
  /*
    A new search starts the vendor list from page 1. Reset here, on the render the debounced search
    arrives, rather than on each keystroke: resetting on the keystroke would ask the vendor for page
    1 of the *old* search first, one wasted request per search.
  */
  const [searchedFor, setSearchedFor] = useState(search);
  if (searchedFor !== search) {
    setSearchedFor(search);
    setAllPage(1);
  }
  const requestedPage = show === 'all' ? allPage : selectedPage;
  const setPage = show === 'all' ? setAllPage : setSelectedPage;

  const list = useOrderablePackagePage(
    dictionariesStatus === 'loading' ? undefined : statusCodes,
    search,
    allPage,
    size,
    listVersion,
  );

  const rows = useMemo(
    () => eligibleOrderablePackages(toPackages(list.rows), statusCodes ?? []),
    [list.rows, toPackages, statusCodes],
  );
  useEffect(() => {
    if (rows.length > 0) onSeen(rows);
  }, [rows, onSeen]);

  // The selection is ours already: "Selected" filters and pages it here.
  const selectedShown = selected.filter((item) => matchesPackagePick(item, searchText));
  const shownTotal = show === 'all' ? list.total : selectedShown.length;
  const page = clampPackagePage(requestedPage, shownTotal, size);

  // A page past the end (the list shrank since it was chosen) goes back to the last real page.
  useEffect(() => {
    if (show === 'all' && list.status === 'ready' && !list.refreshing && page !== requestedPage)
      setAllPage(page);
  }, [show, list.status, list.refreshing, page, requestedPage]);

  const nothingOrderable =
    list.status === 'ready' && !list.refreshing && list.total === 0 && search.trim() === '';
  if (list.status !== 'ready' || (nothingOrderable && show === 'all' && selected.length === 0)) {
    return <PoolState status={list.status} onRetry={list.retry} />;
  }

  return (
    <PackagePicker
      {...rowProps}
      search={searchText}
      searchPlaceholder={t('orders.picker.packages.searchPlaceholderExact')}
      onSearch={(next) => {
        setSearchText(next);
        // "Selected" filters on screen, as you type; the vendor list resets when the search lands.
        setSelectedPage(1);
      }}
      show={show}
      onShow={setShow}
      allCount={list.total}
      selected={selected}
      rows={show === 'all' ? rows : pageOf(selectedShown, page, size)}
      shownTotal={shownTotal}
      page={page}
      pageSize={size}
      onPage={setPage}
      onPageSize={(next) => {
        setSize(next);
        setAllPage(1);
        setSelectedPage(1);
      }}
      // Typing ahead of the debounce counts as busy too, so "No packages match" waits for the answer.
      busy={show === 'all' && (list.refreshing || search !== searchText)}
      onReset={() => {
        setSearchText('');
        setShow('all');
        setAllPage(1);
        setSelectedPage(1);
      }}
    />
  );
}
